import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import "./Complaint.css";
import { useTranslation } from "react-i18next";
import { _t, getCookie } from "../../../../functions/Functions";
import { Helmet } from "react-helmet";
import ComplaintsSidebar from "./ComplaintsSidebar";
import { UserContext } from "../../../../contexts/User";
import { AuditContext } from "../../../../contexts/Audit";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import DataTable from "../../../common/DataTable";
import moment from "moment";
import { renderStatusBadge, renderPriorityBadge } from "./complaintBadges";

export default function ResolvedTicketsList() {
  const { t } = useTranslation();
  const { authUserInfo } = useContext(UserContext);
  const { loading: auditLoading } = useContext(AuditContext);

  const [ticketsList, setTicketsList] = useState({});
  const [ticketsForSearch, setTicketsForSearch] = useState([]);
  const [searchedTicket, setSearchedTicket] = useState({ searched: false, list: [] });
  const [dataPaginating, setDataPaginating] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchRef = useRef();

  useEffect(() => {
    fetchTickets(1);
  }, []);

  const fetchTickets = async (page = 1) => {
    setDataPaginating(true);
    setLoading(true);
    try {
      const url = `${BASE_URL}/complaints?status=resolved&page=${page}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      if (response.data && response.data.length > 0) {
        setTicketsList(response.data[0]);
        setTicketsForSearch(response.data[1] || []);
      } else if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          setTicketsList(response.data.data[0]);
          setTicketsForSearch(response.data.data[1] || []);
        } else {
          setTicketsList(response.data.data);
          setTicketsForSearch(response.data.data.data || []);
        }
      } else if (response.data) {
        setTicketsList(response.data);
        setTicketsForSearch(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching resolved tickets", error);
    } finally {
      setDataPaginating(false);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedTicket({ ...searchedTicket, searched: false });
    } else {
      let searchedList =
        ticketsForSearch?.filter((item) => {
          let lowerCaseTicketNo = (item.ticket_no || item.id || "").toString().toLowerCase();
          let lowerCaseBranch = (item.branch?.name || item.branch_name || "").toLowerCase();
          let lowerCaseSeverity = (item.severity || "").toLowerCase();
          let lowerCasePriority = (item.priority || item.severity_label || "").toLowerCase();
          let lowerCaseStatus = (item.status_label || item.status || "resolved").toLowerCase();
          let lowerCaseRemarks = (item.remarks || "").toLowerCase();
          let lowerCaseCustomer = (item.customer?.name || item.customer_name || "").toLowerCase();
          return (
            lowerCaseTicketNo.includes(searchInput) ||
            lowerCaseBranch.includes(searchInput) ||
            lowerCaseSeverity.includes(searchInput) ||
            lowerCasePriority.includes(searchInput) ||
            lowerCaseStatus.includes(searchInput) ||
            lowerCaseRemarks.includes(searchInput) ||
            lowerCaseCustomer.includes(searchInput)
          );
        }) || [];

      setSearchedTicket({
        list: searchedList,
        searched: true,
      });
    }
  };

  const handleFetchData = (page) => {
    fetchTickets(page);
  };

  const getListData = useCallback(() => {
    if (searchedTicket.searched) {
      return searchedTicket.list;
    }
    return ticketsList?.data || [];
  }, [searchedTicket, ticketsList]);

  const onClearSearch = () => {
    setSearchedTicket({ ...searchedTicket, searched: false });
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  };

  const COLUMNS = [
    {
      label: "ID",
      render: (item, i) =>
        searchedTicket.searched
          ? i + 1
          : i + 1 + ((ticketsList?.current_page || 1) - 1) * (ticketsList?.per_page || 0),
    },
    { label: "Ticket No", render: (item) => item.ticket_no || item.id },
    {
      label: "Branch",
      render: (item) => item.branch?.name || item.branch_name || "-",
    },
    {
      label: "Added Date & Time",
      render: (item) => {
        const rawDate = item.created_at || item.complaint_date || item.date;
        if (!rawDate) return "-";
        const m = moment(rawDate);
        return m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : rawDate;
      },
    },
    { label: "Status", render: (item) => renderStatusBadge(item, t, "Resolved") },
    { label: "Priority", render: (item) => renderPriorityBadge(item, t) },
    { label: "Severity", render: (item) => item.severity || item.severity_label || "-" },
    { label: "Remarks", render: (item) => item.remarks || "-" },
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Resolved Tickets"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                  <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                    <ul className="t-list fk-breadcrumb">
                      <li className="fk-breadcrumb__list">
                        <span className="t-link fk-breadcrumb__link text-capitalize">
                          {!searchedTicket.searched
                            ? _t(t("Resolved Tickets"))
                            : _t(t("Search Result"))}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6 col-lg-7">
                    <div className="row gx-3 align-items-center">
                      <div className="col-md-12 t-mb-15 mb-md-0">
                        <div className="input-group">
                          <div className="form-file">
                            <input
                              ref={searchRef}
                              type="text"
                              className="form-control border-0 form-control--light-1 rounded-left"
                              placeholder={_t(
                                t("Search by ticket no, branch, remarks...")
                              )}
                              onChange={handleSearch}
                              disabled={loading || dataPaginating}
                            />
                          </div>
                          <button className="btn btn-primary rounded-right" type="button">
                            <i className="fa fa-search" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                loading={
                  loading || dataPaginating || auditLoading || authUserInfo.permissions === null
                }
                data={getListData()}
                columns={COLUMNS}
                emptyText="No data available"
                pagination={{
                  isPageLoading: loading || dataPaginating,
                  isPagination: true,
                  pageCount: ticketsList?.last_page,
                  currentPage: ticketsList?.current_page,
                  from: ticketsList?.from,
                  to: ticketsList?.to,
                  total: ticketsList?.total,
                }}
                handleFetchData={handleFetchData}
                searchPayload={{
                  isSearched: searchedTicket.searched,
                  onClearSearch,
                }}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
